export type PipelineDisplayStatus = "IDLE" | "RUNNING" | "COMPLETE" | "BLOCKED" | "SETTLING" | "FAILED";

export type PipelineReasonCategory = "NONE" | "BLOCKED" | "SETTLING";

const SETTLING_REASON_CODES = new Set<string>([
  "CAPTURE_SCOPE_NOT_FROZEN",
  "CAPTURE_JOBS_PENDING",
  "CAPTURE_MANIFESTS_PENDING",
  "CAPTURE_PAGE_COUNT_MOVING",
  "SNAPSHOT_SETTLING",
  "OCR_SCOPE_UNSTABLE",
  "OCR_RECONCILIATION_PENDING",
  "SIGNALS_SCOPE_MISMATCH",
]);

export function normalizeReasonCode(reason: string | null | undefined): string {
  return String(reason ?? "").trim().toUpperCase();
}

export function classifyBlockedReason(reason: string | null | undefined): PipelineReasonCategory {
  const code = normalizeReasonCode(reason);
  if (!code) return "NONE";
  return SETTLING_REASON_CODES.has(code) ? "SETTLING" : "BLOCKED";
}

export function classifyBlockedReasons(reasons: string[] | null | undefined): PipelineReasonCategory {
  const items = Array.isArray(reasons) ? reasons : [];
  if (items.length === 0) return "NONE";
  if (items.some((reason) => classifyBlockedReason(reason) === "SETTLING")) {
    return "SETTLING";
  }
  return "BLOCKED";
}

interface PipelineDisplayStatusInput {
  status?: string | null;
  isRunning?: boolean;
  blockedReasons?: string[] | null;
}

export function getPipelineDisplayStatus({
  status,
  isRunning = false,
  blockedReasons,
}: PipelineDisplayStatusInput): PipelineDisplayStatus {
  const normalizedStatus = String(status ?? "").toUpperCase();

  if (normalizedStatus === "FAILED" || normalizedStatus === "CANCELLED") {
    return "FAILED";
  }

  if (
    normalizedStatus === "COMPLETE" ||
    normalizedStatus === "COMPLETE_WITH_ERRORS" ||
    normalizedStatus === "PARTIAL" ||
    normalizedStatus === "SKIPPED"
  ) {
    return "COMPLETE";
  }

  const reasonCategory = classifyBlockedReasons(blockedReasons);
  if (reasonCategory === "SETTLING") return "SETTLING";
  if (reasonCategory === "BLOCKED") return "BLOCKED";

  if (isRunning || normalizedStatus === "RUNNING" || normalizedStatus === "PENDING" || normalizedStatus === "QUEUED") {
    return "RUNNING";
  }

  return "IDLE";
}

export function getPipelineStatusHelperText(
  displayStatus: PipelineDisplayStatus,
  opts?: { batchAutoRunEnabled?: boolean },
): string | null {
  if (displayStatus === "SETTLING") {
    return "Input scope is still stabilizing; retry will auto-evaluate shortly.";
  }
  if (displayStatus === "BLOCKED") {
    if (opts?.batchAutoRunEnabled === false) {
      return "Prerequisites are not met yet. Batch Auto-Run is off, so downstream steps will not chain automatically.";
    }
    return "Prerequisites are not met yet.";
  }
  if (displayStatus === "FAILED") {
    return "Step ended in terminal failure/cancelled.";
  }
  return null;
}
